import { getCurrencySymbol } from '@repo/utils'
import clsx from 'clsx'
import dayjs from 'dayjs'
import { debounce } from 'lodash'
import React, {
  CSSProperties,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { Control, Controller, RegisterOptions } from 'react-hook-form'
import { FormattedMessage } from 'react-intl'

import s from './styles.module.scss'

import { INPUT_TYPES } from './utils/input-types.enum'
import { ControlledPhoneField } from '../phone-field/ControlledPhoneField'
import { IconSvg, Image } from '../../atoms'
import { CurrencyEnum } from '../../../lib/graphql'
import { decimalPrice } from '../../../utils/currencyFormatter'
import { multiplyByFactor } from '../../../utils/prices'
import { formatTime } from '../../../utils/timeFormatter'

export interface ITextInputProps {
  title?:
    | string
    | React.ReactElement<any, string | React.JSXElementConstructor<any>>
    | undefined
  width?: string | undefined
  rules: Omit<
    RegisterOptions<any, any>,
    'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'
  >
  control: Control<any, any>
  name: string
  style?: CSSProperties
  className?: string
  classNameOnFocus?: string
  placeholder?: string
  units?: string
  disable?: boolean
  image?: string
  formatValue?: (arg: string) => string | undefined
  inputType?: React.HTMLInputTypeAttribute | undefined
  checkInput?: (str: string) => boolean
  min?: number | ((value: number, initialValue: number) => number)
  max?: number
  currency?: CurrencyEnum
  reset?: boolean
  onBlur?: () => void
  inputContainerClassName?: string
  'data-focus'?: boolean
  dataTestId?: string
  debounceDelayMicroSeconds?: number
  value: string | undefined
}

const checkIsEmail = (str: string): boolean => {
  if (str.match(/\s/)) {
    return false
  }
  if (str.match(/\.\./)) {
    return false
  }
  const splitStr = str.split('@')
  if (splitStr.length > 2 || (splitStr.length > 1 && splitStr[0] === '')) {
    return false
  }
  return true
}

const checkIsPrice = (str: string | undefined): boolean => {
  if (str === undefined || str.match(/^-?\d*[,.]?(\d?){2}$/)) {
    return true
  }
  return false
}

const checkIsVat = (str: string): boolean => {
  if (str.match(/^\d*[,.]?(\d?){2}$/)) {
    return true
  }
  return false
}

const checkIsInt = (str: string): boolean => {
  if (str.match(/^\d*$/)) {
    return true
  }
  return false
}

const checkIsFloat = (str: string): boolean => {
  if (str.match(/^\d*[,.]?(\d?){2}$/)) {
    return true
  }
  return false
}

const valueToVat = (value: string) => {
  const valueToArray = value.split('.')
  if (valueToArray.length > 1) {
    if (valueToArray[1].length === 0) {
      return value.concat('00 %')
    }
    if (valueToArray[1].length === 1) {
      return value.concat('0 %')
    }
    if (valueToArray[1].length === 2) {
      return value.concat(' %')
    }
  } else {
    return value.concat(',00 %')
  }
}

const CommonInputText = ({
  name,
  control,
  rules,
  title,
  placeholder,
  style,
  className,
  classNameOnFocus,
  units,
  disable,
  image,
  min,
  max,
  formatValue,
  checkInput,
  inputType,
  reset,
  onBlur,
  inputContainerClassName,
  'data-focus': dataFocus,
  dataTestId,
  debounceDelayMicroSeconds = 0,
  value,
}: ITextInputProps) => {
  const [isFocused, setIsFocused] = useState(false)
  const [initialValue, setInitialValue] = useState('')
  const ref = useRef<HTMLInputElement>(null)
  const [inputValue, setInputValue] = useState(value ?? '')

  useEffect(() => {
    if (dataFocus) {
      ref.current?.focus()
    }
  }, [])

  useEffect(() => {
    if (value && value !== inputValue) {
      setInputValue(value ?? '')
    }
  }, [value])

  const debouncedOnChange = useCallback(
    debounce((onChange, value) => {
      onChange(value)
    }, debounceDelayMicroSeconds),
    [debounceDelayMicroSeconds],
  )

  const updateValue = (onChange: (...event: any[]) => void, value: string) => {
    setInputValue(value) // Update the input value immediately
    debouncedOnChange(onChange, value) // Wait debounce delay to update field value
  }

  return (
    <Controller
      name={name}
      rules={rules}
      control={control}
      render={({ field, fieldState: { error } }) => {
        const handleChange = (e: any) => {
          let value = e.target.value

          if (checkInput) {
            if (
              inputType === INPUT_TYPES.PRICE ||
              inputType === INPUT_TYPES.FLOAT
            ) {
              value = value.replace(',', '.')
            }
            if (inputType === INPUT_TYPES.VAT) {
              value = value.replace(',', '.')
              if (parseFloat(value) > 100) {
                value = '100'
              }
            }

            if (checkInput(value)) {
              updateValue(field.onChange, value)
            }
          } else {
            updateValue(field.onChange, value)
          }
        }

        useEffect(() => {
          if (!initialValue && field.value) {
            setInitialValue(field.value)
          }
        }, [])

        return (
          <div
            className={clsx(
              s.container,
              className,
              isFocused && classNameOnFocus,
              {
                [s.containerReset]: reset,
                [s.errors]: Boolean(error),
              },
            )}
            style={style}
          >
            {title && <h4 className={s.title}>{title}</h4>}
            <div
              className={clsx(s.customInput, inputContainerClassName, {
                [s.disabled]: disable,
              })}
            >
              <div className={s.inputText}>
                {image && (
                  <div className={s.containerImage}>
                    <Image src={image} type='cover' alt='' />
                  </div>
                )}
                <input
                  value={inputValue}
                  onClick={() => setIsFocused(true)}
                  onChange={handleChange}
                  placeholder={placeholder}
                  type={inputType}
                  size={1}
                  disabled={disable}
                  ref={ref}
                  onBlur={() => {
                    setIsFocused(false)
                    let value = inputValue

                    if (value && value !== '') {
                      if (typeof min === 'function') {
                        value = min(
                          parseFloat(value),
                          parseFloat(initialValue),
                        ).toString()
                      } else if (
                        typeof min === 'number' &&
                        parseFloat(value) < min
                      ) {
                        value = min.toString()
                      }

                      if (max !== undefined && parseFloat(value) > max) {
                        value = max.toString()
                      }

                      updateValue(field.onChange, value)
                    }

                    field.onBlur()
                    if (onBlur) {
                      onBlur()
                    }
                  }}
                  data-testid={dataTestId ?? name}
                />
              </div>
              {formatValue &&
                inputValue !== '' &&
                inputValue !== undefined &&
                !isFocused && (
                  <div
                    className={clsx(s.formattedValue, s.customInput, {
                      [s.disabled]: disable,
                    })}
                    onClick={() => {
                      if (ref.current) {
                        ref.current.focus()
                      }
                      if (!disable) {
                        setIsFocused(true)
                      }
                    }}
                  >
                    {formatValue(inputValue)}
                  </div>
                )}
              {units && (
                <div className={s.units}>
                  <span>{units}</span>
                </div>
              )}
            </div>
            {reset && (
              <div
                className={clsx(s.reset, { [s.disabled]: disable })}
                onClick={() => !disable && field.onChange('')}
              >
                <IconSvg.Reset />
                <span>
                  <FormattedMessage
                    defaultMessage='Réinitialiser'
                    id='ui.CommonInputText.reset'
                  />
                </span>
              </div>
            )}
            {error && error.message && (
              <span className={s.form__error}>{error.message}</span>
            )}
          </div>
        )
      }}
    />
  )
}

const InputTypeText = ({
  inputType,
  placeholder,
  ...props
}: ITextInputProps) => {
  switch (inputType) {
    case INPUT_TYPES.PRICE:
      return (
        <CommonInputText
          {...props}
          formatValue={(value) => {
            return value === '' || value === undefined
              ? undefined
              : decimalPrice(multiplyByFactor(value), {
                  currency: props.currency,
                })
          }}
          checkInput={checkIsPrice}
          placeholder={
            placeholder || `0,00 ${getCurrencySymbol(props.currency)}`
          }
          inputType={inputType}
        />
      )
    case INPUT_TYPES.VAT:
      return (
        <CommonInputText
          {...props}
          formatValue={(value) => valueToVat(value)}
          checkInput={checkIsVat}
          placeholder={placeholder ?? '0,00 %'}
          inputType={inputType}
        />
      )
    case INPUT_TYPES.EMAIL:
      return (
        <CommonInputText
          {...props}
          checkInput={checkIsEmail}
          rules={{
            ...props.rules,
            pattern: {
              value: /^\S+@\S+\.\S+$/g,
              message: 'Format requis : xxxx@xxxx.xxxx',
            },
          }}
          placeholder={placeholder}
        />
      )
    case INPUT_TYPES.PHONE:
      return <ControlledPhoneField {...props} />
    case INPUT_TYPES.INT:
      return (
        <CommonInputText
          {...props}
          checkInput={checkIsInt}
          rules={{
            ...props.rules,
            pattern: {
              value: /^\d*$/g,
              message: 'Format requis : Nombre entier',
            },
          }}
          inputType='number'
          placeholder={placeholder}
        />
      )
    case INPUT_TYPES.FLOAT:
      return (
        <CommonInputText
          {...props}
          checkInput={checkIsFloat}
          rules={{
            ...props.rules,
            pattern: {
              value: /^\d*[,.]?(\d?){2}$/g,
              message: 'Format requis : Nombre',
            },
          }}
          inputType={inputType}
          placeholder={placeholder}
        />
      )
    case INPUT_TYPES.TIME:
      return (
        <CommonInputText
          {...props}
          formatValue={(value) => {
            return formatTime(value) ?? ''
          }}
          inputType={inputType}
          placeholder={placeholder}
        />
      )
    case INPUT_TYPES.DATE:
      return (
        <CommonInputText
          {...props}
          formatValue={(value) => {
            return dayjs(value).format('DD/MM/YYYY')
          }}
          inputType={inputType}
          placeholder={placeholder}
        />
      )
    case INPUT_TYPES.OBJECT_ID:
      return (
        <CommonInputText
          {...props}
          rules={{
            ...props.rules,
            pattern: {
              value: /^[a-f\d]{24}$/gi,
              message: 'Format requis : ObjectID',
            },
          }}
        />
      )
    default:
      return <CommonInputText {...props} placeholder={placeholder} />
  }
}

export default InputTypeText
